import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, Video, CheckCircle, AlertCircle, X, Mail, Paperclip, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MyBookings = () => {
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showBlockTimeModal, setShowBlockTimeModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    body: ''
  });
  const [blockTimeData, setBlockTimeData] = useState({
    date: '',
    isFullDay: false,
    startTime: '',
    endTime: '',
    clientName: '',
    programName: '',
    notes: ''
  });
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch bookings from backend
  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/bookings/my-bookings`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setBookings(response.data.bookings || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'confirmed':
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTypeIcon = (type) => {
    const typeLower = type?.toLowerCase();
    switch (typeLower) {
      case 'virtual':
        return <Video className="w-4 h-4 text-blue-600" />;
      case 'in-person':
        return <MapPin className="w-4 h-4 text-green-600" />;
      case 'hybrid':
        return <Users className="w-4 h-4 text-purple-600" />;
      default:
        return <Calendar className="w-4 h-4 text-gray-600" />;
    }
  };

  const upcomingBookings = bookings.filter(b => {
    const status = b.status?.toLowerCase();
    return status === 'confirmed' || status === 'pending';
  });
  const completedBookings = bookings.filter(b => b.status?.toLowerCase() === 'completed');

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  const handleSendMessage = (booking) => {
    setSelectedBooking(booking);
    setEmailData({
      to: booking.contactPerson,
      subject: `Re: ${booking.title}`,
      body: `Dear ${booking.contactPerson.split(' - ')[0]},\n\n`
    });
    setShowEmailModal(true);
  };

  const handleSendEmail = () => {
    // Email sending logic would go here
    console.log('Sending email:', emailData);
    alert('Email sent successfully!');
    setShowEmailModal(false);
    setEmailData({ to: '', subject: '', body: '' });
  };

  const handleCalendarViewClick = () => {
    setActiveTab('calendar');
  };

  const handleBlockTimeSlot = () => {
    setBlockTimeData({
      date: '',
      isFullDay: false,
      startTime: '',
      endTime: '',
      clientName: '',
      programName: '',
      notes: ''
    });
    setSelectedTimeSlots([]);
    setShowBlockTimeModal(true);
  };

  const handleTimeSlotToggle = (time) => {
    if (selectedTimeSlots.includes(time)) {
      setSelectedTimeSlots(selectedTimeSlots.filter(t => t !== time));
    } else {
      setSelectedTimeSlots([...selectedTimeSlots, time]);
    }
  };

  const handleSaveBlockedTime = () => {
    // Save logic would go here
    console.log('Blocked time data:', {
      ...blockTimeData,
      selectedTimeSlots
    });
    alert('Time slot blocked successfully!');
    setShowBlockTimeModal(false);
  };

  // Generate hourly time slots
  const timeSlots = [];
  for (let hour = 0; hour < 24; hour++) {
    const time = `${hour.toString().padStart(2, '0')}:00`;
    timeSlots.push(time);
  }

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600">Loading your bookings...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center justify-center space-y-4">
              <AlertCircle className="w-16 h-16 text-red-500" />
              <div>
                <p className="text-gray-900 font-semibold mb-2">Failed to load bookings</p>
                <p className="text-gray-600 mb-4">{error}</p>
                <Button onClick={fetchBookings} className="bg-green-600 hover:bg-green-700">
                  Try Again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>My Bookings</span>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handleCalendarViewClick}>
                Calendar View
              </Button>
              <Button 
                size="sm" 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleBlockTimeSlot}
              >
                Block Time Slot
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="upcoming">
                Upcoming ({upcomingBookings.length})
              </TabsTrigger>
              <TabsTrigger value="completed">
                Completed ({completedBookings.length})
              </TabsTrigger>
              <TabsTrigger value="calendar">
                Calendar View
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming" className="space-y-4 mt-6">
              {upcomingBookings.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg mb-2">No upcoming bookings</p>
                  <p className="text-gray-500 text-sm">Your confirmed and pending bookings will appear here</p>
                </div>
              ) : (
                upcomingBookings.map((booking) => (
                <Card key={booking.id} className="border-2 hover:border-green-200 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-semibold text-gray-900">{booking.title}</h3>
                          <div className="flex items-center space-x-2">
                            <Badge className={getStatusColor(booking.status)}>
                              {getStatusIcon(booking.status)}
                              <span className="ml-1">{booking.status}</span>
                            </Badge>
                            <span className="text-2xl font-bold text-green-600">{booking.fee}</span>
                          </div>
                        </div>
                        
                        <p className="text-blue-600 font-medium text-lg mb-3">{booking.client}</p>
                        <p className="text-gray-600 mb-4">{booking.description}</p>
                        
                        <div className="grid grid-cols-2 gap-6 mb-4">
                          <div className="space-y-3">
                            <div className="flex items-center text-gray-700">
                              <Calendar className="w-5 h-5 mr-3 text-blue-500" />
                              <span className="font-medium">{booking.date}</span>
                            </div>
                            <div className="flex items-center text-gray-700">
                              <Clock className="w-5 h-5 mr-3 text-green-500" />
                              <span>{booking.time}</span>
                            </div>
                            <div className="flex items-center text-gray-700">
                              {getTypeIcon(booking.type)}
                              <span className="ml-3">{booking.location}</span>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex items-center text-gray-700">
                              <Users className="w-5 h-5 mr-3 text-purple-500" />
                              <span>{booking.participants} participants</span>
                            </div>
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">Contact:</span> {booking.contactPerson}
                            </div>
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">Type:</span>
                              <Badge variant="outline" className="ml-2">
                                {booking.type}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        {booking.notes && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                            <p className="text-sm text-yellow-800">
                              <span className="font-medium">Notes:</span> {booking.notes}
                            </p>
                          </div>
                        )}
                        
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Required Materials:</p>
                          <div className="flex flex-wrap gap-2">
                            {booking.materials.map((material, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {material}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleViewDetails(booking)}
                          >
                            View Details
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleSendMessage(booking)}
                          >
                            Send Message
                          </Button>
                          <Button size="sm" variant="outline">
                            Reschedule
                          </Button>
                          {booking.status === 'Pending' && (
                            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
              )}
            </TabsContent>

            <TabsContent value="completed" className="space-y-4 mt-6">
              {completedBookings.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg mb-2">No completed bookings</p>
                  <p className="text-gray-500 text-sm">Completed training sessions will appear here</p>
                </div>
              ) : (
                completedBookings.map((booking) => (
                <Card key={booking.id} className="border-2">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-semibold text-gray-900">{booking.title}</h3>
                          <div className="flex items-center space-x-2">
                            <Badge className={getStatusColor(booking.status)}>
                              {getStatusIcon(booking.status)}
                              <span className="ml-1">{booking.status}</span>
                            </Badge>
                            <span className="text-xl font-bold text-gray-600">{booking.fee}</span>
                          </div>
                        </div>
                        
                        <p className="text-blue-600 font-medium mb-2">{booking.client}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                          <span>{booking.date}</span>
                          <span>•</span>
                          <span>{booking.time}</span>
                          <span>•</span>
                          <span>{booking.participants} participants</span>
                        </div>
                        
                        {booking.notes && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                            <p className="text-sm text-green-800">{booking.notes}</p>
                          </div>
                        )}
                        
                        <div className="flex items-center space-x-3">
                          <Button size="sm" variant="outline">
                            Generate Invoice
                          </Button>
                          <Button size="sm" variant="outline">
                            Get Testimonial
                          </Button>
                          <Button size="sm" variant="outline">
                            Book Follow-up
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
              )}
            </TabsContent>

            <TabsContent value="calendar" className="mt-6">
              <Card>
                <CardContent className="p-6">
                  <div className="mb-6 flex items-center justify-between">
                    <h3 className="text-lg font-semibold">March 2024</h3>
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm">Previous</Button>
                      <Button variant="outline" size="sm">Today</Button>
                      <Button variant="outline" size="sm">Next</Button>
                    </div>
                  </div>
                  
                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-2 mb-4">
                    {/* Calendar Headers */}
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="p-2 text-center font-medium text-gray-600 bg-gray-50 rounded">
                        {day}
                      </div>
                    ))}
                    
                    {/* Calendar Days */}
                    {Array.from({ length: 35 }, (_, i) => {
                      const dayNumber = i - 2; // Starting from March 1st (assuming March 1st is on a Friday)
                      const isCurrentMonth = dayNumber > 0 && dayNumber <= 31;
                      const hasBooking = [15, 18, 22, 25].includes(dayNumber);
                      const isToday = dayNumber === 14;
                      
                      return (
                        <div
                          key={i}
                          className={`min-h-[100px] p-2 border rounded-lg transition-colors ${
                            isCurrentMonth
                              ? 'bg-white border-gray-200 hover:border-green-300'
                              : 'bg-gray-50 text-gray-400 border-gray-100'
                          } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                        >
                          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : ''}`}>
                            {isCurrentMonth ? dayNumber : ''}
                          </div>
                          
                          {hasBooking && isCurrentMonth && (
                            <div className="space-y-1">
                              {dayNumber === 15 && (
                                <div className="text-xs bg-green-100 text-green-800 rounded px-2 py-1 truncate">
                                  Leadership Workshop
                                </div>
                              )}
                              {dayNumber === 18 && (
                                <div className="text-xs bg-blue-100 text-blue-800 rounded px-2 py-1 truncate">
                                  Remote Training
                                </div>
                              )}
                              {dayNumber === 22 && (
                                <div className="text-xs bg-purple-100 text-purple-800 rounded px-2 py-1 truncate">
                                  Innovation Workshop
                                </div>
                              )}
                              {dayNumber === 25 && (
                                <div className="text-xs bg-orange-100 text-orange-800 rounded px-2 py-1 truncate">
                                  Sales Coaching
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Calendar Legend */}
                  <div className="flex items-center justify-center space-x-6 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
                      <span>Confirmed</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded"></div>
                      <span>Pending</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
                      <span>Virtual</span>
                    </div>
                  </div>
                  
                  <div className="mt-6 text-center">
                    <div className="flex justify-center space-x-3">
                      <Button variant="outline">Sync with Google Calendar</Button>
                      <Button variant="outline">Sync with Outlook</Button>
                      <Button variant="outline">Export Calendar</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Booking Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-6 mt-4">
              {/* Header Section */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedBooking.title}
                    </h2>
                    <p className="text-lg text-blue-600 font-medium">
                      {selectedBooking.client}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge className={getStatusColor(selectedBooking.status)}>
                      {getStatusIcon(selectedBooking.status)}
                      <span className="ml-1">{selectedBooking.status}</span>
                    </Badge>
                    <p className="text-3xl font-bold text-green-600 mt-2">
                      {selectedBooking.fee}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700">{selectedBooking.description}</p>
              </div>

              {/* Date & Time Section */}
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                      Schedule
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium">{selectedBooking.date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Time:</span>
                        <span className="font-medium">{selectedBooking.time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Duration:</span>
                        <span className="font-medium">8 hours</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-purple-500" />
                      Participants
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Count:</span>
                        <span className="font-medium">{selectedBooking.participants} people</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type:</span>
                        <Badge variant="outline">{selectedBooking.type}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Contact:</span>
                        <span className="font-medium text-xs">{selectedBooking.contactPerson.split(' - ')[0]}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Location Section */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-green-500" />
                    Location & Logistics
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-600">Venue:</span>
                      <p className="font-medium">{selectedBooking.location}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getTypeIcon(selectedBooking.type)}
                      <span className="text-sm font-medium">{selectedBooking.type} Session</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Materials Section */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Required Materials</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {selectedBooking.materials.map((material, idx) => (
                      <div key={idx} className="flex items-center space-x-2 bg-gray-50 p-3 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium">{material}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Notes Section */}
              {selectedBooking.notes && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Special Notes</h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-gray-700">{selectedBooking.notes}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Payment Information */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Payment Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Training Fee:</span>
                      <span className="font-bold text-green-600">{selectedBooking.fee}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payment Status:</span>
                      <Badge variant="outline" className="bg-green-50">Paid</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Invoice #:</span>
                      <span className="font-medium">INV-2024-{selectedBooking.id}001</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payment Date:</span>
                      <span className="font-medium">2024-02-28</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Preparation Checklist */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Preparation Checklist</h3>
                  <div className="space-y-2">
                    {[
                      'Review participant profiles',
                      'Prepare presentation materials',
                      'Test equipment and technology',
                      'Confirm venue access',
                      'Send pre-work to participants'
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                        <input type="checkbox" className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                  Close
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleSendMessage(selectedBooking);
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Block Time Slot Modal */}
      <Dialog open={showBlockTimeModal} onOpenChange={setShowBlockTimeModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center">
              <Calendar className="w-6 h-6 mr-2 text-green-600" />
              Block Time Slot
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Date Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Date *
              </label>
              <Input
                type="date"
                value={blockTimeData.date}
                onChange={(e) => setBlockTimeData({ ...blockTimeData, date: e.target.value })}
                className="w-full"
                required
              />
            </div>

            {/* Full Day Option */}
            <div className="flex items-center space-x-3 bg-blue-50 p-4 rounded-lg">
              <input
                type="checkbox"
                id="fullDay"
                checked={blockTimeData.isFullDay}
                onChange={(e) => {
                  setBlockTimeData({ ...blockTimeData, isFullDay: e.target.checked });
                  if (e.target.checked) {
                    setSelectedTimeSlots(timeSlots);
                  } else {
                    setSelectedTimeSlots([]);
                  }
                }}
                className="w-5 h-5 text-green-600 rounded"
              />
              <label htmlFor="fullDay" className="text-sm font-medium text-gray-700 cursor-pointer">
                Block Full Day (00:00 - 23:59)
              </label>
            </div>

            {/* Time Slots Selection */}
            {!blockTimeData.isFullDay && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Time Slots (Hourly Basis) *
                </label>
                <div className="grid grid-cols-6 gap-2 max-h-60 overflow-y-auto border rounded-lg p-4 bg-gray-50">
                  {timeSlots.map((time) => {
                    const isSelected = selectedTimeSlots.includes(time);
                    return (
                      <button
                        key={time}
                        type="button"
                        onClick={() => handleTimeSlotToggle(time)}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isSelected
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {time}
                      </button>
                    );
                  })}
                </div>
                {selectedTimeSlots.length > 0 && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: {selectedTimeSlots.length} hour(s) - 
                    {selectedTimeSlots.sort().length > 0 && (
                      <span className="font-medium ml-1">
                        {selectedTimeSlots.sort()[0]} to {selectedTimeSlots.sort()[selectedTimeSlots.length - 1]}
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Client and Program Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Name *
                </label>
                <Input
                  value={blockTimeData.clientName}
                  onChange={(e) => setBlockTimeData({ ...blockTimeData, clientName: e.target.value })}
                  placeholder="Enter client/organization name"
                  className="w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Program/Workshop Name *
                </label>
                <Input
                  value={blockTimeData.programName}
                  onChange={(e) => setBlockTimeData({ ...blockTimeData, programName: e.target.value })}
                  placeholder="Enter program or workshop name"
                  className="w-full"
                  required
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes (Optional)
              </label>
              <Textarea
                value={blockTimeData.notes}
                onChange={(e) => setBlockTimeData({ ...blockTimeData, notes: e.target.value })}
                placeholder="Add any special requirements, preparation notes, or other details..."
                className="w-full min-h-[100px] resize-y"
              />
            </div>

            {/* Summary Card */}
            {blockTimeData.date && (selectedTimeSlots.length > 0 || blockTimeData.isFullDay) && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    Blocking Summary
                  </h4>
                  <div className="space-y-1 text-sm text-gray-700">
                    <p><span className="font-medium">Date:</span> {blockTimeData.date}</p>
                    <p>
                      <span className="font-medium">Time:</span>{' '}
                      {blockTimeData.isFullDay 
                        ? 'Full Day (00:00 - 23:59)' 
                        : `${selectedTimeSlots.length} hour(s) selected`}
                    </p>
                    {blockTimeData.clientName && (
                      <p><span className="font-medium">Client:</span> {blockTimeData.clientName}</p>
                    )}
                    {blockTimeData.programName && (
                      <p><span className="font-medium">Program:</span> {blockTimeData.programName}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowBlockTimeModal(false)}
              >
                Cancel
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleSaveBlockedTime}
                disabled={
                  !blockTimeData.date || 
                  (!blockTimeData.isFullDay && selectedTimeSlots.length === 0) ||
                  !blockTimeData.clientName ||
                  !blockTimeData.programName
                }
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Block Time Slot
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Compose Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 flex items-center">
              <Mail className="w-5 h-5 mr-2 text-blue-600" />
              Compose Message
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* To Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To:
              </label>
              <Input
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                placeholder="Recipient email"
                className="w-full"
              />
              {selectedBooking && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedBooking.contactPerson}
                </p>
              )}
            </div>

            {/* Subject Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject:
              </label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                placeholder="Email subject"
                className="w-full"
              />
            </div>

            {/* Message Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message:
              </label>
              <Textarea
                value={emailData.body}
                onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                placeholder="Type your message here..."
                className="w-full min-h-[250px] resize-y"
              />
            </div>

            {/* Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments:
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-green-400 transition-colors cursor-pointer">
                <Paperclip className="w-6 h-6 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  Click to attach files or drag and drop
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  PDF, DOC, DOCX, PPT, PPTX up to 10MB
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-500">
                {selectedBooking && `Re: ${selectedBooking.title}`}
              </div>
              <div className="flex items-center space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailData({ to: '', subject: '', body: '' });
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleSendEmail}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyBookings;